import React, { useState, useEffect, useRef, useCallback } from 'react';
import { devError } from '../../lib/devLog';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Upload, Grid3x3, List, Search, Image as ImageIcon, X, Trash2, Download, CheckCircle, AlertCircle, FileText } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { withAdminAuth } from '../../lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MediaLibrary = () => {
  const { token } = useAuth();
  const [viewMode, setViewMode] = useState('grid');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    fetchMediaFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMediaFiles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/media`);
      setMediaFiles(response.data.media || []);
    } catch (error) {
      devError('Error fetching media:', error);
      showNotification('Failed to load media files', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  // Handle file upload
  const uploadFiles = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    
    // Add all files to FormData
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });

    // Initialize progress tracking
    const progressArray = Array.from(files).map(file => ({
      name: file.name,
      progress: 0,
      status: 'uploading'
    }));
    setUploadProgress(progressArray);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/media/upload-multiple`,
        formData,
        withAdminAuth({
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(prev =>
              prev.map(item => ({
                ...item,
                progress: percentCompleted,
                status: 'uploading'
              }))
            );
          }
        })
      );

      // Mark all as complete
      setUploadProgress(prev =>
        prev.map(item => ({
          ...item,
          progress: 100,
          status: 'complete'
        }))
      );

      showNotification(`Successfully uploaded ${response.data.count} file(s)`, 'success');
      
      // Refresh media list
      await fetchMediaFiles();
      
      // Clear progress after delay
      setTimeout(() => {
        setUploadProgress([]);
        setUploading(false);
      }, 1500);

    } catch (error) {
      devError('Upload error:', error);
      setUploadProgress(prev =>
        prev.map(item => ({
          ...item,
          status: 'error'
        }))
      );
      showNotification('Failed to upload files', 'error');
      setUploading(false);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = dropZoneRef.current?.getBoundingClientRect();
    if (rect && (
      e.clientX <= rect.left ||
      e.clientX >= rect.right ||
      e.clientY <= rect.top ||
      e.clientY >= rect.bottom
    )) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      uploadFiles(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // File input handler
  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      uploadFiles(files);
    }
    // Reset input
    e.target.value = '';
  };

  // Delete file
  const deleteFile = async (file) => {
    if (!window.confirm(`Delete ${file.filename}?`)) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/media/${file.path}`,
        withAdminAuth()
      );
      showNotification('File deleted successfully', 'success');
      await fetchMediaFiles();
    } catch (error) {
      devError('Delete error:', error);
      const msg = error?.response?.status === 401
        ? 'Session expired — please log in again'
        : 'Failed to delete file';
      showNotification(msg, 'error');
    }
  };

  // Filter media by search
  const filteredMedia = mediaFiles.filter(file =>
    file.filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get file icon/preview
  const getFilePreview = (file) => {
    if (file.type === 'images') {
      return (
        <img
          src={`${BACKEND_URL}${file.url}`}
          alt={file.filename}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      );
    }
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <FileText className="w-12 h-12 text-gray-400" />
      </div>
    );
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-light mb-2">Media Library</h1>
        <p className="text-gray-600">Manage your images and files</p>
      </div>

      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 ${
              notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search and View Mode */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          <div className="flex gap-2 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid' ? 'bg-black text-white' : 'hover:bg-gray-100'
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list' ? 'bg-black text-white' : 'hover:bg-gray-100'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Upload Drop Zone */}
      <div
        ref={dropZoneRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-12 mb-6 text-center cursor-pointer transition-all ${
          isDragging
            ? 'border-black bg-gray-50 scale-[1.02]'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInput}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.txt"
        />
        
        <Upload className={`w-12 h-12 mx-auto mb-4 transition-colors ${
          isDragging ? 'text-black' : 'text-gray-400'
        }`} />
        
        <p className="text-lg mb-2 font-medium">
          {isDragging ? 'Drop files here' : 'Drag and drop files here'}
        </p>
        <p className="text-sm text-gray-600">
          or click to browse from your computer
        </p>
        <p className="text-xs text-gray-500 mt-2">
          Supports: Images (JPG, PNG, GIF, WebP), Documents (PDF, DOC, TXT)
        </p>
      </div>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mb-6 space-y-2">
          {uploadProgress.map((file, index) => (
            <div key={`${file.name}-${index}`} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium truncate flex-1">{file.name}</span>
                <span className="text-sm text-gray-600 ml-4">{file.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  className={`h-2 rounded-full ${
                    file.status === 'complete' ? 'bg-green-500' :
                    file.status === 'error' ? 'bg-red-500' :
                    'bg-black'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${file.progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Media Grid/List */}
      {filteredMedia.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p>{searchQuery ? 'No files found matching your search' : 'No files uploaded yet'}</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {filteredMedia.map((file) => (
            <motion.div
              key={file.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="aspect-square">
                {getFilePreview(file)}
              </div>
              
              <div className="p-3">
                <p className="text-sm font-medium truncate" title={file.filename}>
                  {file.filename}
                </p>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              </div>

              {/* Hover Actions */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <a
                  href={`${BACKEND_URL}${file.url}`}
                  download
                  className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteFile(file);
                  }}
                  className="p-2 bg-white rounded-full hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {filteredMedia.map((file, index) => (
            <div
              key={file.id}
              className={`flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors ${
                index !== filteredMedia.length - 1 ? 'border-b border-gray-200' : ''
              }`}
            >
              <div className="w-16 h-16 rounded overflow-hidden flex-shrink-0">
                {getFilePreview(file)}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{file.filename}</p>
                <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
              </div>

              <div className="flex gap-2">
                <a
                  href={`${BACKEND_URL}${file.url}`}
                  download
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={() => deleteFile(file)}
                  className="p-2 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
